import { Grid, GridItem, useDisclosure } from '@chakra-ui/react';
import { IContractWithQuotes } from '@monetize/types/app';
import { toDateShort } from '@monetize/utils/core';
import { ROUTES } from '../../../constants';
import {
  CONTRACT_END_ACTION_UI_DISPLAY,
  CONTRACT_STATUS_DISPLAY,
} from '../../../constants/contracts';
import { ACCOUNTS_READ_ROLES } from '../../../services/acl/roles';
import ContractModal from '../../Contracts/ContractModal';
import { MBox } from '../chakra';
import { MCurrency } from '../MCurrency';
import { UserAvatarAndName } from '../MCustomSelect/components/UserAvatarAndName';
import MFormField from '../MFormField';
import { MFormFieldReadOnly } from '../MFormFieldReadOnly';
import { LookupTriggerContent } from './LookupTriggerContent';
import { RecordLookupPopover } from './RecordLookupPopover';
import { RecordLookupPopoverHeader } from './RecordLookupPopoverHeader';

export interface ContractLookupPopoverProps {
  value: string;
  displayLabel?: string;
  getRouteFn?: (record: IContractWithQuotes) => string;
}

export function ContractLookupPopover({
  value,
  displayLabel,
  getRouteFn = (record) =>
    ROUTES.getAccountContractEditRoute(record.accountId, record.id),
}: ContractLookupPopoverProps) {
  const { open, onOpen, onClose } = useDisclosure();

  if (!value) {
    return null;
  }

  return (
    // When used in data-tables, prevent clicks from propagating to parent
    <MBox as="span" onClick={(ev) => ev.stopPropagation()}>
      {open && value && (
        <ContractModal
          isOpen
          contractId={value}
          onClose={() => {
            onClose();
          }}
        />
      )}
      <RecordLookupPopover
        recordId={value}
        endpoint={`/api/contracts/${value}`}
        buttonProps={{ lineClamp: 3 }}
        renderRecord={(record) => {
          const contract = record as IContractWithQuotes;
          return (
            <>
              <RecordLookupPopoverHeader
                recordId={contract.id}
                href={getRouteFn(contract)}
                recordName={() =>
                  `${toDateShort(contract.startDate)} - ${toDateShort(contract.endDate)}`
                }
                displayStatus={CONTRACT_STATUS_DISPLAY[contract.status]}
                secondaryDisplayStatus={
                  contract.renewed ? 'Renewed' : undefined
                }
                // Contracts are available to anyone that can also view accounts
                roles={ACCOUNTS_READ_ROLES}
                linkProps={{
                  onClick: (ev) => {
                    // Let cmd/ctrl/shift + click fall through to the anchor so
                    // the record opens in a new tab/window natively.
                    // (RecordLookupPopoverHeader stops propagation for us.)
                    if (ev.metaKey || ev.ctrlKey || ev.shiftKey) {
                      return;
                    }
                    ev.preventDefault();
                    onOpen();
                  },
                }}
              />

              <Grid gridTemplateColumns="1fr 1fr" rowGap={4} mt={4}>
                <GridItem colSpan={2}>
                  <UserAvatarAndName
                    userName={contract.ownerName}
                    textProps={{ fontSize: 'sm' }}
                  />
                </GridItem>
                <GridItem>
                  <MFormField isReadOnly label="Contract Value">
                    <MCurrency
                      amount={contract.totalValue}
                      options={{
                        currency: contract.currency,
                      }}
                    />
                  </MFormField>
                </GridItem>
                <GridItem>
                  <MFormFieldReadOnly label="Renewal Action">
                    {CONTRACT_END_ACTION_UI_DISPLAY[contract.endAction]}
                  </MFormFieldReadOnly>
                </GridItem>
              </Grid>
            </>
          );
        }}
      >
        <LookupTriggerContent displayLabel={displayLabel} value={value} />
      </RecordLookupPopover>
    </MBox>
  );
}
